import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
} from '@mui/material';
import { Grid } from '../components/common/TypedGrid';
import {
  Save,
  Business,
  LocalShipping,
  Schedule,
} from '@mui/icons-material';
import { useAdmin } from '../contexts/AdminContext';
import adminService, { AppSettings } from '../services/adminService';
import { PageHeader } from '../components/common';


const Settings: React.FC = () => {
  const { refreshDashboardStats } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<AppSettings>({
    id: 'main',
    generalSettings: {
      companyName: 'PickupNinja',
      supportEmail: 'support@pickupninja.com',
      supportPhone: '+91 9876543210',
      maxDeliveryRadius: 50,
      defaultCurrency: 'INR',
      timezone: 'Asia/Kolkata',
      gstNumber: '',
      gstPercentage: 0,
      platformCharge: {
        type: 'percentage',
        value: 5,
      },
      payoutCharge: {
        type: 'flat',
        value: 10,
      },
      maxDeliveryDistance: 50,
      supportContact: {
        email: 'support@pickupninja.com',
        phone: '+91-8000-000-000',
      },
      minimumNinjaWithdrawalAmount: 1000, // Issue #167: Default ₹1000
      minimumCustomerWithdrawalAmount: 100,
      // Issue #184: Configurable ninja slot time range
      ninjaSlotStartTime: '8:00 AM',
      ninjaSlotEndTime: '8:00 PM',
      // Issue #316: Cancellation deduction duration (default 0 minutes = no time restriction)
      cancellationDeductionDuration: 0,
    },
    ninjaTravelTime: 30,
    updatedAt: new Date(),
    updatedBy: 'system',
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // Helper function to handle number input validation
  const handleNumberInput = (value: string, min: number = 0, max?: number): number => {
    // Remove any non-numeric characters except decimal point
    const cleanValue = value.replace(/[^0-9.]/g, '');
    
    // Handle empty input
    if (!cleanValue) return min;
    
    // Parse the number
    const numValue = parseFloat(cleanValue);
    
    // Check if it's a valid number
    if (isNaN(numValue)) return min;
    
    // Apply min/max constraints
    if (numValue < min) return min;
    if (max !== undefined && numValue > max) return max;
    
    return numValue;
  };

  // Helper function to validate number input on keypress
  const handleNumberKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'];
    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  };

  // GST Number validation function
  const validateGSTNumber = (gstNumber: string): boolean => {
    if (!gstNumber) return false;
    // GSTIN format: 15 characters (2 state code + 10 PAN + 1 entity + 1 Z + 1 checksum)
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gstNumber.toUpperCase());
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      // Load settings from admin service
      const appSettings = await adminService.getAppSettings();
      if (appSettings) {
        // Ensure charge fields exist with defaults
        const generalSettings = appSettings.generalSettings || {};
        const settingsWithDefaults = {
          ...appSettings,
          generalSettings: {
            ...generalSettings,
            // Ensure fields with no fallback at render time are always defined,
            // so a partial settings document can't flip these inputs from
            // controlled to uncontrolled.
            companyName: generalSettings.companyName || 'PickupNinja',
            supportEmail: generalSettings.supportEmail || 'support@pickupninja.com',
            supportPhone: generalSettings.supportPhone || '+91 9876543210',
            defaultCurrency: generalSettings.defaultCurrency || 'INR',
            timezone: generalSettings.timezone || 'Asia/Kolkata',
            gstNumber: generalSettings.gstNumber || '',
            gstPercentage: generalSettings.gstPercentage || 0,
            platformCharge: generalSettings.platformCharge || {
              type: 'percentage' as const,
              value: 5,
            },
            payoutCharge: generalSettings.payoutCharge || {
              type: 'flat' as const,
              value: 10,
            },
            // Ensure supportContact exists to avoid undefined property access
            supportContact: generalSettings.supportContact || {
              email: 'support@pickupninja.com',
              phone: '+91-8000-000-000',
            },
            // Issue #167: Default minimum withdrawal amount
            minimumNinjaWithdrawalAmount: generalSettings.minimumNinjaWithdrawalAmount || 1000,
            minimumCustomerWithdrawalAmount: generalSettings.minimumCustomerWithdrawalAmount || 100,
            // Issue #184: Default ninja slot time range
            ninjaSlotStartTime: generalSettings.ninjaSlotStartTime || '8:00 AM',
            ninjaSlotEndTime: generalSettings.ninjaSlotEndTime || '8:00 PM',
            // Issue #316: Default cancellation deduction duration
            cancellationDeductionDuration: generalSettings.cancellationDeductionDuration ?? 0,
          },
          ninjaTravelTime: appSettings.ninjaTravelTime || 30,
        };
        setSettings(settingsWithDefaults);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Use default settings which already have the charge fields
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await adminService.updateAppSettings(settings);
      showSnackbar('Settings saved successfully', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showSnackbar('Error saving settings', 'error');
    } finally {
      setSaving(false);
    }
  };


  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const updateGeneralSetting = (key: string, value: any) => {
    console.log(`Updating ${key}:`, value); // Debug log
    setSettings(prevSettings => ({
      ...prevSettings,
      generalSettings: {
        ...prevSettings.generalSettings,
        [key]: value,
      },
    }));
  };

  const updatePlatformCharge = (type: 'flat' | 'percentage', value: number) => {
    setSettings({
      ...settings,
      generalSettings: {
        ...settings.generalSettings,
        platformCharge: { type, value },
      },
    });
  };

  const updatePayoutCharge = (type: 'flat' | 'percentage', value: number) => {
    setSettings({
      ...settings,
      generalSettings: {
        ...settings.generalSettings,
        payoutCharge: { type, value },
      },
    });
  };

  const updateSupportContact = (field: 'email' | 'phone', value: string) => {
    setSettings(prev => ({
      ...prev,
      generalSettings: {
        ...prev.generalSettings,
        supportContact: {
          ...(prev.generalSettings?.supportContact || { email: '', phone: '' }),
          [field]: value,
        },
      },
    }));
  };


  return (
    <Box>
      <PageHeader title="Settings" subtitle="Company details, payments and app configuration" />
      <Grid container >
        {/* General Settings - Full Width */}
        <Grid xs={12}>
          <Card sx={{ height: 'fit-content' }}>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Business sx={{ mr: 2, color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h5" fontWeight={700} color="text.primary">
                  General Settings
                </Typography>
              </Box>
              
              {/* Two Column Layout */}
              <Grid container sx={{ alignItems: 'flex-start' }} spacing={3}>
                {/* Left Column - Company Information */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom sx={{ mb: 1 }}>
                      Company Information
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      <TextField
                        fullWidth
                        label="Company Name"
                        value={settings.generalSettings.companyName}
                        onChange={(e) => updateGeneralSetting('companyName', e.target.value)}
                        variant="outlined"
                        size="medium"
                      />
                      
                      <TextField
                        fullWidth
                        label="Support Email"
                        type="email"
                        value={settings.generalSettings.supportEmail}
                        onChange={(e) => updateGeneralSetting('supportEmail', e.target.value)}
                        variant="outlined"
                        size="medium"
                      />
                      
                      <TextField
                        fullWidth
                        label="Support Phone"
                        value={settings.generalSettings.supportPhone}
                        onChange={(e) => updateGeneralSetting('supportPhone', e.target.value)}
                        variant="outlined"
                        size="medium"
                      />
                      
                      <TextField
                        fullWidth
                        label="Max Delivery Radius (km)"
                        type="number"
                        value={settings.generalSettings.maxDeliveryRadius || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            updateGeneralSetting('maxDeliveryRadius', '');
                          } else {
                            const numValue = parseInt(value, 10);
                            if (!isNaN(numValue) && numValue >= 0) {
                              updateGeneralSetting('maxDeliveryRadius', numValue);
                            }
                          }
                        }}
                        variant="outlined"
                        size="medium"
                        helperText="⚙️ Default Global Setting - Maximum delivery radius for all areas (can be overridden per area)"
                        inputProps={{ min: 0, step: 1 }}
                      />
                      
                      <TextField
                        fullWidth
                        label="Max Delivery Distance (km)"
                        type="number"
                        value={settings.generalSettings.maxDeliveryDistance || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            updateGeneralSetting('maxDeliveryDistance', '');
                          } else {
                            const numValue = parseInt(value, 10);
                            if (!isNaN(numValue) && numValue >= 0) {
                              updateGeneralSetting('maxDeliveryDistance', numValue);
                            }
                          }
                        }}
                        variant="outlined"
                        size="medium"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 0, step: 1 }}
                      />
                      
                      <TextField
                        fullWidth
                        label="Ninja Travel Time (minutes)"
                        type="text"
                        value={settings.ninjaTravelTime ?? 30}
                        onChange={(e) => setSettings({ ...settings, ninjaTravelTime: Math.floor(handleNumberInput(e.target.value, 0)) })}
                        onKeyPress={handleNumberKeyPress}
                        variant="outlined"
                        size="medium"
                        helperText="Extra time (in minutes) added to each task for ninja travel"
                      />
                      
                      <TextField
                        fullWidth
                        label="Support Contact Email"
                        type="email"
                        value={settings.generalSettings.supportContact?.email || ''}
                        onChange={(e) => updateSupportContact('email', e.target.value)}
                        variant="outlined"
                        size="medium"
                      />
                      
                      <TextField
                        fullWidth
                        label="Support Contact Phone"
                        value={settings.generalSettings.supportContact?.phone || ''}
                        onChange={(e) => updateSupportContact('phone', e.target.value)}
                        variant="outlined"
                        size="medium"
                      />
                      
                      <FormControl fullWidth variant="outlined" size="medium">
                        <InputLabel>Default Currency</InputLabel>
                        <Select
                          value={settings.generalSettings.defaultCurrency}
                          label="Default Currency"
                          onChange={(e) => updateGeneralSetting('defaultCurrency', e.target.value)}
                        >
                          <MenuItem value="INR">INR (₹)</MenuItem>
                          <MenuItem value="USD">USD ($)</MenuItem>
                          <MenuItem value="EUR">EUR (€)</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <FormControl fullWidth variant="outlined" size="medium">
                        <InputLabel>Timezone</InputLabel>
                        <Select
                          value={settings.generalSettings.timezone}
                          label="Timezone"
                          onChange={(e) => updateGeneralSetting('timezone', e.target.value)}
                        >
                          <MenuItem value="Asia/Kolkata">Asia/Kolkata</MenuItem>
                          <MenuItem value="America/New_York">America/New_York</MenuItem>
                          <MenuItem value="Europe/London">Europe/London</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Grid>

                {/* Right Column - GST, Platform & Payout Configuration */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* GST Section */}
                    <Box>
                      <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom sx={{ mb: 4 }}>
                        GST Configuration
                      </Typography>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField
                          fullWidth
                          label="GST Number (GSTIN)"
                          value={settings.generalSettings.gstNumber ?? ''}
                          onChange={(e) => {
                            const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                            console.log('GST Number changed:', value); // Debug log
                            updateGeneralSetting('gstNumber', value);
                            // Reset GST percentage if GST number becomes invalid
                            if (!validateGSTNumber(value)) {
                              updateGeneralSetting('gstPercentage', 0);
                            }
                          }}
                          variant="outlined"
                          size="medium"
                          placeholder="22AAAAA0000A1Z5"
                          helperText="Format: 15 characters (e.g., 22AAAAA0000A1Z5)"
                          error={settings.generalSettings.gstNumber ? !validateGSTNumber(settings.generalSettings.gstNumber) : false}
                          inputProps={{
                            maxLength: 15,
                            style: { textTransform: 'uppercase' }
                          }}
                          autoComplete="off"
                        />
                        
                        <TextField
                          fullWidth
                          label="GST Percentage (%)"
                          type="text"
                          value={settings.generalSettings.gstPercentage || 0}
                          onChange={(e) => updateGeneralSetting('gstPercentage', handleNumberInput(e.target.value, 0, 100))}
                          onKeyPress={handleNumberKeyPress}
                          variant="outlined"
                          size="medium"
                          disabled={!validateGSTNumber(settings.generalSettings.gstNumber || '')}
                          helperText={!validateGSTNumber(settings.generalSettings.gstNumber || '') ? "Enter a valid GST number to enable this field" : "Enter GST percentage (0-100%)"}
                        />
                      </Box>
                    </Box>

                    {/* Platform Charge Section */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom sx={{ mb: 2 }}>
                        Platform Charge
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <FormControl variant="outlined" size="medium" sx={{ minWidth: 150, flex: '0 0 150px' }}>
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={settings.generalSettings.platformCharge?.type || 'percentage'}
                            label="Type"
                            onChange={(e) => updatePlatformCharge(e.target.value as 'flat' | 'percentage', settings.generalSettings.platformCharge?.value || 0)}
                          >
                            <MenuItem value="flat">Flat Amount</MenuItem>
                            <MenuItem value="percentage">Percentage</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          label={(settings.generalSettings.platformCharge?.type || 'percentage') === 'flat' ? 'Amount (₹)' : 'Percentage (%)'}
                          type="text"
                          value={settings.generalSettings.platformCharge?.value || 0}
                          onChange={(e) => updatePlatformCharge(settings.generalSettings.platformCharge?.type || 'percentage', handleNumberInput(e.target.value, 0))}
                          onKeyPress={handleNumberKeyPress}
                          variant="outlined"
                          size="medium"
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    </Box>

                    {/* Payout Charge Section */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom sx={{ mb: 2 }}>
                        Payout Charge
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <FormControl variant="outlined" size="medium" sx={{ minWidth: 150, flex: '0 0 150px' }}>
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={settings.generalSettings.payoutCharge?.type || 'flat'}
                            label="Type"
                            onChange={(e) => updatePayoutCharge(e.target.value as 'flat' | 'percentage', settings.generalSettings.payoutCharge?.value || 0)}
                          >
                            <MenuItem value="percentage">Percentage</MenuItem>
                            <MenuItem value="flat">Flat Amount</MenuItem>
                          </Select>
                        </FormControl>
                        <TextField
                          label={(settings.generalSettings.payoutCharge?.type || 'flat') === 'percentage' ? 'Percentage (%)' : 'Amount (₹)'}
                          type="text"
                          value={settings.generalSettings.payoutCharge?.value || 0}
                          onChange={(e) => updatePayoutCharge(settings.generalSettings.payoutCharge?.type || 'flat', handleNumberInput(e.target.value, 0))}
                          onKeyPress={handleNumberKeyPress}
                          variant="outlined"
                          size="medium"
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    </Box>

                    {/* Issue #167: Minimum Ninja Withdrawal Amount */}
                    <Box sx={{ mb: 1, mt: 3 }}>
                      <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom sx={{ mb: 2 }}>
                        Ninja Wallet Settings
                      </Typography>
                      <TextField
                        fullWidth
                        label="Minimum Ninja Withdrawal Amount (₹)"
                        type="text"
                        value={settings.generalSettings.minimumNinjaWithdrawalAmount || 1000}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          generalSettings: {
                            ...prev.generalSettings,
                            minimumNinjaWithdrawalAmount: handleNumberInput(e.target.value, 100, 100000),
                          },
                        }))}
                        onKeyPress={handleNumberKeyPress}
                        variant="outlined"
                        size="medium"
                        helperText="Ninjas must withdraw at least this amount. Default: ₹1000. Min: ₹100, Max: ₹1,00,000"
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1, color: 'text.secondary' }}>₹</Typography>
                          ),
                        }}
                      />
                      <TextField
                        fullWidth
                        sx={{ mt: 2 }}
                        label="Minimum Customer Withdrawal Amount (₹)"
                        type="text"
                        value={settings.generalSettings.minimumCustomerWithdrawalAmount || 100}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          generalSettings: {
                            ...prev.generalSettings,
                            minimumCustomerWithdrawalAmount: handleNumberInput(e.target.value, 1, 100000),
                          },
                        }))}
                        onKeyPress={handleNumberKeyPress}
                        variant="outlined"
                        size="medium"
                        helperText="Customers can take money out of their wallet in amounts of at least this much. Default: ₹100."
                        InputProps={{
                          startAdornment: (
                            <Typography sx={{ mr: 1, color: 'text.secondary' }}>₹</Typography>
                          ),
                        }}
                      />
                    </Box>

                    {/* Issue #184: Ninja Slot Time Configuration */}
                    {/* Issue #306: Aligned Ninja Slot Time Range section with other sections */}
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom sx={{ mb: 2 }}>
                        Ninja Slot Time Range
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                        <FormControl variant="outlined" size="medium" sx={{ flex: 1 }}>
                          <InputLabel>Slot Start Time</InputLabel>
                          <Select
                            value={settings.generalSettings.ninjaSlotStartTime || '8:00 AM'}
                            label="Slot Start Time"
                            onChange={(e) => updateGeneralSetting('ninjaSlotStartTime', e.target.value)}
                          >
                            <MenuItem value="5:00 AM">5:00 AM</MenuItem>
                            <MenuItem value="5:30 AM">5:30 AM</MenuItem>
                            <MenuItem value="6:00 AM">6:00 AM</MenuItem>
                            <MenuItem value="6:30 AM">6:30 AM</MenuItem>
                            <MenuItem value="7:00 AM">7:00 AM</MenuItem>
                            <MenuItem value="7:30 AM">7:30 AM</MenuItem>
                            <MenuItem value="8:00 AM">8:00 AM</MenuItem>
                            <MenuItem value="8:30 AM">8:30 AM</MenuItem>
                            <MenuItem value="9:00 AM">9:00 AM</MenuItem>
                            <MenuItem value="9:30 AM">9:30 AM</MenuItem>
                            <MenuItem value="10:00 AM">10:00 AM</MenuItem>
                          </Select>
                        </FormControl>
                        <FormControl variant="outlined" size="medium" sx={{ flex: 1 }}>
                          <InputLabel>Slot End Time</InputLabel>
                          <Select
                            value={settings.generalSettings.ninjaSlotEndTime || '8:00 PM'}
                            label="Slot End Time"
                            onChange={(e) => updateGeneralSetting('ninjaSlotEndTime', e.target.value)}
                          >
                            <MenuItem value="5:00 PM">5:00 PM</MenuItem>
                            <MenuItem value="5:30 PM">5:30 PM</MenuItem>
                            <MenuItem value="6:00 PM">6:00 PM</MenuItem>
                            <MenuItem value="6:30 PM">6:30 PM</MenuItem>
                            <MenuItem value="7:00 PM">7:00 PM</MenuItem>
                            <MenuItem value="7:30 PM">7:30 PM</MenuItem>
                            <MenuItem value="8:00 PM">8:00 PM</MenuItem>
                            <MenuItem value="8:30 PM">8:30 PM</MenuItem>
                            <MenuItem value="9:00 PM">9:00 PM</MenuItem>
                            <MenuItem value="9:30 PM">9:30 PM</MenuItem>
                            <MenuItem value="10:00 PM">10:00 PM</MenuItem>
                            <MenuItem value="10:30 PM">10:30 PM</MenuItem>
                            <MenuItem value="11:00 PM">11:00 PM</MenuItem>
                            <MenuItem value="12:00 AM">12:00 AM</MenuItem>
                            <MenuItem value="1:00 AM">1:00 AM</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Default: 8:00 AM to 8:00 PM. Affects slot availability shown to customers when booking ninjas.
                      </Typography>
                    </Box>

                    {/* Issue #316: Cancellation Deduction Duration */}
                    <Box sx={{ mb: 1, mt: 3 }}>
                      <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom sx={{ mb: 2 }}>
                        Cancellation Deduction Duration
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Configure when cancellation deductions apply (in minutes before slot start). Set to 0 to allow free cancellation anytime.
                      </Typography>
                      <TextField
                        fullWidth
                        label="Deduction Duration (minutes)"
                        type="number"
                        value={settings.generalSettings.cancellationDeductionDuration ?? 0}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '') {
                            updateGeneralSetting('cancellationDeductionDuration', 0);
                          } else {
                            const numValue = parseInt(value, 10);
                            if (!isNaN(numValue) && numValue >= 0) {
                              updateGeneralSetting('cancellationDeductionDuration', numValue);
                            }
                          }
                        }}
                        variant="outlined"
                        size="medium"
                        helperText="0 = Free cancellation anytime. Set to N minutes to apply deduction within N minutes before slot start."
                        inputProps={{ min: 0, step: 1 }}
                      />
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {/* Save Button - Full Width at Bottom */}
              <Box sx={{ 
                mt: 2, 
                pt: 2, 
                borderTop: '1px solid', 
                borderColor: 'divider' 
              }}>
                <Button
                  fullWidth
                  startIcon={<Save />}
                  onClick={handleSaveSettings}
                  variant="contained"
                  size="large"
                  disabled={saving}
                  sx={{
                    py: 2,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    borderRadius: 2,
                    textTransform: 'none',
                  }}
                >
                  {saving ? 'Saving Settings...' : 'Save Settings'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;